import { defineStore } from 'pinia'

import { computed, reactive, ref } from 'vue'

import { AuthApi } from '@/features/api/services/authApi.js'
import { decideRedirectionPointByRole } from '@/features/auth/decideRedirectionPointByRole.js'
import { useAuthRedirection } from '@/features/auth/useAuthRedirection.js'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { useRegisterStore } from '@/features/auth/views/register/useRegisterStore.js'
import { getActiveClubId } from '@/features/router/useActiveClub.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'
import { useToaster } from '@/features/ui/toaster/useToaster'

export const UserIsNotExist = 'User_Is_Not_Exist'
export const AccountIsNotExist = 'Account_Is_Not_Exist'

export const AlreadyRegisteredThroughGoogleMessage =
  'User_Has_Been_Already_Registered_Through_Google'

export const useLoginStore = defineStore('loginStore', function () {
  const authStore = useAuthStore()
  const registerStore = useRegisterStore()
  const isLoading = ref(false)
  const redirectCallback = ref(null)
  const loginEmailEnterUserIsNotExistCallback = ref(null)

  const STEPS = {
    EMAIL_ENTER: 1,
    PASSWORD_ENTER: 2,
    GOOGLE_LOGIN_WARNING: 3,
  }
  const currentStep = ref(STEPS.EMAIL_ENTER)
  const needsOTP = ref(false)

  const isEmailEnterStep = computed(() => currentStep.value === STEPS.EMAIL_ENTER)
  const isPasswordEnterStep = computed(() => currentStep.value === STEPS.PASSWORD_ENTER)
  const isGoogleLoginWarningView = computed(() => currentStep.value === STEPS.GOOGLE_LOGIN_WARNING)

  const errorText = ref('')

  const formValues = reactive({
    email: '',
    password: '',
    otpCode: '',
    rememberMe: false,
  })

  const { redirectUrl } = useAuthRedirection()

  const router = useClubRouter()
  const toaster = useToaster()

  async function handleEmailEnter() {
    errorText.value = ''

    try {
      isLoading.value = true

      const response = await AuthApi.loginWithEmail({
        email: formValues.email,
        tenantId: getActiveClubId(),
        password: formValues.password,
      })

      if (response.codeSent) {
        needsOTP.value = true
        currentStep.value = STEPS.PASSWORD_ENTER
        return
      }

      if (!response.auth_token) {
        console.error('No auth token in response', response)
        toaster.error({
          title: 'Error',
          detail: 'No auth token in response',
        })
        return
      }

      await authStore.setUserToken(response.auth_token, {
        email: formValues.email,
        password: formValues.password,
      })

      await decideNavigation()
    } catch (e) {
      console.log(e)
      const errorData = e?.response?.data

      if (errorData === UserIsNotExist || errorData === AccountIsNotExist) {
        registerStore.setEmail(formValues.email)

        if (loginEmailEnterUserIsNotExistCallback.value) {
          loginEmailEnterUserIsNotExistCallback.value({ errorData })
          return
        }

        console.log('User is not exist, redirect to register')

        await router.push('/auth/register?redirectUrl=' + redirectUrl)
        return
      }

      errorText.value = errorData

      if (errorData === AlreadyRegisteredThroughGoogleMessage) {
        console.log('User has been already registered through google')
        setCurrentStep(STEPS.GOOGLE_LOGIN_WARNING)
      }
    } finally {
      isLoading.value = false
    }
  }

  async function decideNavigation() {
    const myDetailsResponse = await authStore.getMyDetailsWithoutCache(false)

    if (redirectCallback.value) {
      redirectCallback.value({ myDetailsResponse })
      return
    }

    console.log('redirectUrl: ', redirectUrl)

    if (getActiveClubId()) {
      console.log('Redirection to club ->', redirectUrl)
      await router.push(redirectUrl)
      return
    }

    if (!myDetailsResponse) {
      toaster.error({
        title: 'Error',
        detail: 'No myDetailsResponse',
      })

      console.error('No myDetailsResponse')
      return
    }

    const url = decideRedirectionPointByRole(myDetailsResponse?.type)
    console.log('Redirection to point ->', url)
    await router.push(url)
  }

  async function handlePasswordEnter() {
    try {
      isLoading.value = true
      const loginResponse = await AuthApi.loginWithEmail({
        email: formValues.email,
        password: formValues.password,
        code: formValues.otpCode,
        tenantId: getActiveClubId(),
      })

      if (!loginResponse.auth_token) {
        console.error('No auth token in response', loginResponse)
        toaster.error({
          title: 'Error',
          detail: 'No auth token in response',
        })
        return
      }

      await authStore.setUserToken(loginResponse.auth_token, {
        email: formValues.email,
        password: formValues.password,
      })

      await decideNavigation()
    } catch (e) {
      console.log(e)
      const errorData = e?.response?.data

      if (errorData === 'Invalid_Username_Or_Password') {
        currentStep.value = STEPS.EMAIL_ENTER
      }

      errorText.value = e?.response?.data

      formValues.otpCode = ''
    } finally {
      isLoading.value = false
    }
  }

  function handleClickStepBack() {
    if (isPasswordEnterStep.value) {
      currentStep.value = STEPS.EMAIL_ENTER
      formValues.otpCode = ''
    }
  }

  function setFormValues(values) {
    Object.assign(formValues, values)
  }

  function setCurrentStep(step) {
    currentStep.value = step
  }

  function setRedirectCallback(callback) {
    redirectCallback.value = callback
  }

  function setLoginEmailEnterUserIsNotExistCallback(callback) {
    loginEmailEnterUserIsNotExistCallback.value = callback
  }

  return {
    STEPS,
    setCurrentStep,
    currentStep,

    isEmailEnterStep,
    isPasswordEnterStep,
    isGoogleLoginWarningView,

    handleEmailEnter,
    handlePasswordEnter,

    handleClickStepBack,

    setFormValues,
    formValues,

    isLoading,

    needsOTP,

    setRedirectCallback,
    redirectCallback,

    setLoginEmailEnterUserIsNotExistCallback,
    loginEmailEnterUserIsNotExistCallback,

    decideNavigation,

    errorText,
  }
})
